<template>
  <ion-page>
    <ion-header :translucent="true">
      <ion-toolbar>
      </ion-toolbar>
    </ion-header>

    <ion-content :fullscreen="true" class="landing-content">


      <!-- Overview back link -->
      <div class="overview-link" @click="onClick('chessvis_landing_page',t('Welcome to Chessvis'),'program')">
        <ion-icon :icon="chevronBackOutline" class="overview-link-icon" aria-hidden="true" />
        <span>{{ t("ChessVis Overview") }}</span>
      </div>

      <!-- Hero -->
      <section class="hero">
        <div class="hero-icon-wrap">
          <ion-icon :icon="barbellOutline" class="hero-icon" aria-hidden="true" />
        </div>
        <h1 class="hero-title">{{ t("Tactics Gym") }}</h1>
        <p class="hero-tagline">{{ t("The sharpest chess training tools in one place — completely free.") }}</p>
        <p class="hero-sub">{{ t("Puzzles, board visualization, and blindfold chess. Train the skills that separate good players from great ones.") }}</p>
      </section>

      <!-- Puzzles -->
      <section class="feature-section">
        <div class="section-header">
          <div class="section-icon-wrap teal">
            <ion-icon :icon="extensionPuzzleOutline" aria-hidden="true" />
          </div>
          <h2 class="section-title">{{ t("Puzzles") }}</h2>
        </div>

        <div class="feature-card">
          <h3>{{ t("4.5 Million Puzzles — and Counting") }}</h3>
          <p>{{ t("ChessVis starts with the entire Lichess puzzle library — over 4.5 million categorized and rated puzzles — and builds on top of it with features you won't find anywhere else.") }}</p>
        </div>

        <div class="feature-card highlight">
          <div class="feature-card-label">{{ t("Unique to ChessVis") }}</div>
          <h3>{{ t("Solve the Puzzle. Visualized In Your Mind.") }}</h3>
          <p>{{ t("We show you a real game position, a few moves before the tactic strikes. Play it forward in your mind's eye. Can you see the board clearly enough to find the winning move?") }}</p>
        </div>

        <div class="feature-card">
          <h3>{{ t("You Control the Difficulty") }}</h3>
          <p>{{ t("Set both the category and rating of every puzzle you face. Easy forks one day, hard multi-move mates the next. You'll never be locked into puzzles around some rating you've 'earned' — the full collection is always available.") }}</p>
        </div>

        <div class="feature-card">
          <h3>{{ t("The No-Repeat Pledge") }}</h3>
          <p>{{ t("With 4.5 million puzzles, you'll never see the same one twice. Work offline with up to 2,000 downloaded puzzles. Track your history, graph your results, and compare with others.") }}</p>
        </div>
      </section>

      <!-- Visualization -->
      <section class="feature-section">
        <div class="section-header">
          <div class="section-icon-wrap teal">
            <ion-icon :icon="eyeOutline" aria-hidden="true" />
          </div>
          <h2 class="section-title">{{ t("Visualization") }}</h2>
        </div>

        <p class="section-intro">{{ t("ChessVis started as a visualization app — it's in the name. These tools train the mental seeing that strong players rely on.") }}</p>

        <div class="feature-card highlight">
          <div class="feature-card-label">{{ t("Unique to ChessVis") }}</div>
          <h3>{{ t("Who's Guarding Whom") }}</h3>
          <p>{{ t("A deceptively simple exercise that forces you to mentally track piece movements and interactions. You won't find this anywhere else, and it's more powerful than it looks.") }}</p>
        </div>

        <div class="feature-card">
          <h3>{{ t("Move Following") }}</h3>
          <p>{{ t("Track a sequence of moves and update the board in your mind. Follow from the opening, from a random midgame position, or focus on specific pieces as they move across the board.") }}</p>
        </div>

        <div class="feature-card">
          <h3>{{ t("Static Boards") }}</h3>
          <p>{{ t("Study a position, then recreate it from memory. Start simple — a few pieces — and work your way up. A quiet but effective way to sharpen your board sense.") }}</p>
        </div>
      </section>

      <!-- Blindfold -->
      <section class="feature-section">
        <div class="section-header">
          <div class="section-icon-wrap teal">
            <ion-icon :icon="eyeOffOutline" aria-hidden="true" />
          </div>
          <h2 class="section-title">{{ t("Blindfold Chess") }}</h2>
        </div>

        <p class="section-intro">{{ t("Play a full game against the computer without seeing the board. ChessVis makes this approachable with a gradual stepping-stone system.") }}</p>

        <div class="feature-card highlight">
          <div class="feature-card-label">{{ t("Smart onboarding") }}</div>
          <h3>{{ t("Stepping Stones") }}</h3>
          <p>{{ t("Start with all pieces sharing one color, then progress to two-color disks, a shared color disk, and finally a fully empty board. Each step builds the mental model you need for the next.") }}</p>
        </div>

        <div class="feature-card">
          <h3>{{ t("Reveal When You Need It") }}</h3>
          <p>{{ t("Feeling lost? Reveal the board on demand, or set it to reveal automatically every few moves as a checkpoint. You're always in control of how hard you push.") }}</p>
        </div>
      </section>

      <!-- CTA -->
      <section class="cta-section">
        <ion-button
          expand="block"
          class="cta-button"
          @click="onClick('tactics_gym_menu','Tactics Gym','program')"
        >
          {{ t("Start Training") }}
        </ion-button>
        <p class="cta-note">{{ t("Puzzles & Visualization always free. And so is beginning blindfold chess. No account required to begin.") }}</p>
      </section>

    </ion-content>

    <BottomNav @bottom-click="onClick"/>
  </ion-page>
</template>

<script setup lang="ts">
import {
  IonPage, IonHeader, IonToolbar, IonTitle,
  IonContent, IonIcon, IonButton,
} from '@ionic/vue'
import {
  barbellOutline, extensionPuzzleOutline, eyeOutline, eyeOffOutline, chevronBackOutline
} from 'ionicons/icons'
import BottomNav from '@/components/BottomNav.vue'
import { t } from '@/composables/translateText.js'

const emit = defineEmits(['menu-item-click'])

function onClick(path : string,label : string,whichMenu : string) {
  //console.log("Landing page CLICK ",path,label)
  emit("menu-item-click",path,label,whichMenu)
}

</script>

<style scoped>
ion-toolbar {
  --background: #12122a;
  --color: #ffffff;
  --border-color: #2a2a4a;
}

ion-title {
  font-size: 17px;
  font-weight: 500;
}

ion-content {
  --background: #1a1a2e;
}

/* ── Overview link ── */
.overview-link {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 16px;
  background: #0f0f22;
  border-bottom: 0.5px solid #2a2a4a;
  cursor: pointer;
  font-size: 13px;
  color: #AFA9EC;
}
 
.overview-link-icon {
  font-size: 15px;
}

/* ── Hero ── */
.hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 36px 24px 28px;
  border-bottom: 0.5px solid #2a2a4a;
}

.hero-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 18px;
  background: #085041;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.hero-icon {
  font-size: 34px;
  color: #9FE1CB;
}

.hero-title {
  font-size: 26px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 10px;
}

.hero-tagline {
  font-size: 16px;
  font-weight: 500;
  color: #9FE1CB;
  margin: 0 0 10px;
  line-height: 1.4;
}

.hero-sub {
  font-size: 14px;
  color: #888880;
  line-height: 1.6;
  margin: 0;
}

/* ── Feature sections ── */
.feature-section {
  padding: 24px 16px 8px;
  border-bottom: 0.5px solid #2a2a4a;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.section-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.section-icon-wrap.teal {
  background: #085041;
  color: #9FE1CB;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
}

.section-intro {
  font-size: 14px;
  color: #888880;
  line-height: 1.6;
  margin: 0 0 14px;
}

/* ── Feature cards ── */
.feature-card {
  background: #1e1e35;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 10px;
  border: 0.5px solid #2a2a4a;
}

.feature-card.highlight {
  border-color: #1D9E75;
  background: #0d2a22;
}

.feature-card-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #1D9E75;
  margin-bottom: 6px;
}

.feature-card h3 {
  font-size: 15px;
  font-weight: 600;
  color: #e0e0f0;
  margin: 0 0 6px;
}

.feature-card p {
  font-size: 13px;
  color: #b0b0bc;
  line-height: 1.6;
  margin: 0;
}

.feature-card em {
  color: #9FE1CB;
  font-style: normal;
  font-weight: 500;
}

/* ── CTA ── */
.cta-section {
  padding: 28px 20px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.cta-button {
  --background: #1D9E75;
  --background-activated: #168a64;
  --color: #ffffff;
  --border-radius: 14px;
  font-size: 16px;
  font-weight: 600;
  height: 52px;
  width: 100%;
}

.cta-note {
  font-size: 12px;
  color: #888880;
  margin: 0;
  text-align: center;
}
</style>
